import { SerializedEditorState } from 'lexical'
import { useCallback, useMemo, useState } from 'react'

import useListGlCodesAndGroups from './administration/useListGlCodesAndGroups'
import useDate, {
  IDataType as IDateDataType,
  IOptionType as IDateOptionType,
} from './date'

export interface IOptionType {
  dateOptions?: IDateOptionType
}

type IMetricsType = (string | { key: string; operator: '+' | '-' })[]

export interface IDataType extends IDateDataType {
  pnlRankings?: Record<
    'category' | 'rankBy',
    {
      value: string[][]
      onChange: (value: string[][]) => void
      options: {
        id: string
        parentId: string
        displayName: string
        sortingPreference?: 'ASC' | 'DESC'
      }[]
    }
  > & {
    metrics: IMetricsType
    salesMetrics: IMetricsType
    sorting?: {
      key: string
      desc: boolean
    }
    columns: {
      key: string
      title: string
      sortable: true
    }[]
  }
}

const usePnlRankings = ({ dateOptions = {} }: IOptionType) => {
  const { date } = useDate(dateOptions)
  const categoryHook = useState<string[][]>([])
  const pnlRankingHook = useState<string[][]>([])
  const { data } = useListGlCodesAndGroups()

  const categories = useMemo(
    () =>
      !data
        ? []
        : [
            { id: 'groups', parentId: 'root', displayName: 'Categories' },
            ...(data?.listGlGroups.nodes || []).map((l) => ({
              id: `folder-${l.id}`,
              parentId: 'groups',
              displayName: l.name,
              sortingPreference: l.sortingPreference,
            })),
            { id: 'gl-codes', parentId: 'root', displayName: 'GL Code' },
            ...(data?.listGlCodes.nodes || []).map((l) => ({
              id: `item-${l.id}`,
              parentId: 'gl-codes',
              displayName: `${l.code}-${l.name}`,
              sortingPreference: l.sortingPreference,
            })),
          ],
    [data],
  )

  const getFolderIds = useCallback(
    (id: string): string[] => [
      id,
      ...(data?.listGlGroups.nodes || [])
        .filter((n) => n.parentGroupId === id)
        .map((n) => getFolderIds(n.id))
        .flat(),
    ],
    [data],
  )
  const getMetrics = useCallback(
    (id: string): IMetricsType => {
      if (!id) return []

      if (/^item-/.test(id))
        return (data?.listGlCodes.nodes || [])
          .filter((n) => id.replace(/^item-/, '') === n.id)
          .map((n) => n.name)

      const group = (data?.listGlGroups.nodes || []).find(
        (n) => n.id === id.replace(/^folder-/, ''),
      )

      if (group?.formula) {
        const allFormula = (
          group.formula as unknown as SerializedEditorState<{
            type: string
            version: number
            children: Record<string, unknown>[]
          }>
        ).root.children
          .map((d) => d.children)
          .flat() as {
          type: 'select-node' | 'text'
          text: string
          moreDetails: { id: string }
        }[]

        return allFormula
          .map((f, index, allData) => {
            switch (f.type) {
              case 'select-node': {
                const operator =
                  index === 0 || allData[index - 1]?.text.trim() !== '-'
                    ? '+'
                    : '-'

                return getMetrics(f.moreDetails.id).map((key) => ({
                  key,
                  operator,
                })) as IMetricsType
              }
              default:
                return []
            }
          })
          .flat()
      }

      const folderIds = getFolderIds(id.replace(/^folder-/, ''))

      return (data?.listGlCodes.nodes || [])
        .filter((n) => folderIds.includes(n.parentGroupId))
        .map((n) => n.name)
    },
    [data, getFolderIds],
  )
  const salesMetrics = useMemo(() => {
    const salesId = data?.listGlGroups.nodes.find((n) => n.name === 'Sales')?.id

    if (!salesId) return []

    return getMetrics(`folder-${salesId}`)
  }, [data])

  return {
    date,
    pnlRankings: useMemo((): IDataType['pnlRankings'] => {
      const { displayType, toDateDisplayType } = date?.getInfo(0) ?? {}
      const period = displayType || 'Period'
      const ptd = toDateDisplayType || 'PTD'
      const pnlRankings = [
        'pAndLCurrentCompare',
        'pAndLYoyCompare',
        'pAndLLYYtdCompare',
        'pAndLTrailing3Period',
      ].map((column) => ({
        id: column,
        parentId: 'root',
        displayName: {
          pAndLCurrentCompare: 'Current',
          pAndLYoyCompare: `Current ${period} vs PY ${ptd}`,
          pAndLLYYtdCompare: 'Current YTD vs Prior YTD',
          pAndLTrailing3Period: `Trailing 3 ${period} Trend`,
        }[column] as string,
      }))

      const category = categories.find(
        (c) => c.id === categoryHook[0]?.[0]?.slice(-1)[0],
      )
      const desc =
        (category && 'sortingPreference' in category
          ? category.sortingPreference
          : 'ASC') === 'DESC'
      const pnlRanking =
        pnlRankings.find((o) => o.id === pnlRankingHook[0]?.[0]?.[0])?.id || ''

      return {
        category: {
          value: categoryHook[0],
          onChange: categoryHook[1],
          options: categories,
        },
        rankBy: {
          value: pnlRankingHook[0],
          onChange: pnlRankingHook[1],
          options: pnlRankings,
        },
        metrics: getMetrics(categoryHook[0][0]?.slice(-1)[0] || ''),
        salesMetrics,
        sorting: {
          pAndLCurrentCompare: {
            key: 'pAndLSales',
            desc,
          },
          pAndLYoyCompare: {
            key: 'pAndLSales',
            desc,
          },
          pAndLLYYtdCompare: {
            key: 'pAndLYtdSales',
            desc,
          },
          pAndLTrailing3Period: {
            key: 'pAndLSales',
            desc,
          },
        }[pnlRanking],
        columns: {
          pAndLCurrentCompare: [
            {
              key: 'current',
              title: 'Current',
              children: [
                {
                  key: 'pAndLSales',
                  align: 'center',
                  title: '$',
                  sortable: true,
                },
                {
                  key: 'pAndLSalesPercent',
                  align: 'center',
                  title: '%',
                  sortable: true,
                },
              ],
            },
          ],
          pAndLYoyCompare: [
            {
              key: 'current',
              title: 'Current',
              children: [
                {
                  key: 'pAndLSales',
                  align: 'center',
                  title: '$',
                  sortable: true,
                },
                {
                  key: 'pAndLSalesPercent',
                  align: 'center',
                  title: '%',
                  sortable: true,
                },
              ],
            },
            {
              key: 'pyPtd',
              title: `PY ${ptd}`,
              children: [
                {
                  key: 'pAndLYoySales',
                  align: 'center',
                  title: '$',
                  sortable: true,
                },
                {
                  key: 'pAndLYoySalesPercent',
                  align: 'center',
                  title: '%',
                  sortable: true,
                },
              ],
            },
            {
              key: 'pAndLYoyPercent',
              title: 'Difference',
              sortable: true,
            },
          ],
          pAndLLYYtdCompare: [
            {
              key: 'currentYtd',
              title: 'Current YTD',
              children: [
                {
                  key: 'pAndLYtdSales',
                  align: 'center',
                  title: '$',
                  sortable: true,
                },
                {
                  key: 'pAndLYtdSalesPercent',
                  align: 'center',
                  title: '%',
                  sortable: true,
                },
              ],
            },
            {
              title: 'PY YTD',
              key: 'pyYtd',
              children: [
                {
                  key: 'pAndLLyYtdSales',
                  align: 'center',
                  title: '$',
                  sortable: true,
                },
                {
                  key: 'pAndLLyYtdSalesPercent',
                  align: 'center',
                  title: '%',
                  sortable: true,
                },
              ],
            },
            {
              key: 'pAndLLyYtdPercent',
              title: 'Difference',
              sortable: true,
            },
          ],
          pAndLTrailing3Period: [
            {
              key: 'twoPeriodsAgo',
              title: `2 ${period}s Ago`,
              children: [
                {
                  key: 'pAndL2PastSales',
                  align: 'center',
                  title: '$',
                  sortable: true,
                },
                {
                  key: 'pAndL2PastSalesPercent',
                  align: 'center',
                  title: '%',
                  sortable: true,
                },
              ],
            },
            {
              key: 'lastPeriod',
              title: `Last ${period}`,
              children: [
                {
                  key: 'pAndL1PastSales',
                  align: 'center',
                  title: '$',
                  sortable: true,
                },
                {
                  key: 'pAndL1PastSalesPercent',
                  align: 'center',
                  title: '%',
                  sortable: true,
                },
              ],
            },
            {
              key: 'current',
              title: 'Current',
              children: [
                {
                  key: 'pAndLSales',
                  align: 'center',
                  title: '$',
                  sortable: true,
                },
                {
                  key: 'pAndLSalesPercent',
                  align: 'center',
                  title: '%',
                  sortable: true,
                },
              ],
            },
            {
              key: 'pAndLSalesTrand',
              title: 'Difference',
              sortable: true,
            },
          ],
        }[pnlRanking as string] as NonNullable<
          IDataType['pnlRankings']
        >['columns'],
      }
    }, [
      date,
      categoryHook,
      pnlRankingHook,
      categories,
      getMetrics,
      salesMetrics,
    ]),
  }
}

export default usePnlRankings
